import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import DataGridCell from 'components/enhanced/data-grid/components/cell/DataGridCell';
import { filterOperators } from 'components/enhanced/data-grid/utils/filters';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { IsTenantCoordinatorOrAdmin } from 'shared/permissions/IsTenantCoordinator';
import dayjs from 'dayjs';
import TenantPage from 'layouts/TenantPage';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pastStockQuantityService } from '../api/past-stock-quantity.service';
import { baseCell } from 'components/cells';

export const StockQuantitiesAtDate = () => {
  const translationPrefix = 'pages.stock_quantity.list';

  const { t } = useTranslation();
  const datagridRefresh = useRef(null);
  const [date, setDate] = useState(dayjs().subtract(1, 'day'));

  const aggregatedColumns: GridColDef[] = [
    {
      field: 'product',
      headerName: t(`global.product`),
      width: 250,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      width: 300,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'notes',
      headerName: t(`global.notes`),
      width: 250,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'unit_price',
      type: 'string',
      headerName: t(`entities.movement.unit_price`),
      filterable: false,
      sortable: false,
      width: 100,
      valueFormatter: ({ value }) => {
        try {
          return value.map(currencyFormatter).join(',');
        } catch {
          return '';
        }
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <IsTenantCoordinatorOrAdmin>
            <DataGridCell content={params.value.map(currencyFormatter).join(',')} />;
          </IsTenantCoordinatorOrAdmin>
        );
      }
    },
    {
      field: 'category',
      headerName: t(`global.category`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      width: 250,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 250,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'quantity',
      headerName: t(`entities.stock_quantity.total_quantity`),
      width: 150,
      type: 'number',
      filterable: false,
      sortable: false,
      renderCell: baseCell
    }
  ];

  const onChange = (date: dayjs.Dayjs) => {
    if (date.isValid()) setDate(date);
  };

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      menuRight={
        <div className="flex items-center">
          <div className="mr-5">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                format="DD/MM/YYYY"
                label={t('actions.select-date')}
                value={date}
                onChange={onChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'background.paper',
                        transition: 'all 0.2s ease',

                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main'
                        },

                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 2
                        }
                      },

                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem'
                      },

                      '& .MuiInputBase-input': {
                        py: 1.1
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </div>
        </div>
      }>
      <EnhancedDataGrid
        columns={aggregatedColumns}
        service={pastStockQuantityService}
        outerParams={{ date: date.format('YYYY-MM-DD') }}
        refresh={datagridRefresh}
      />
    </TenantPage>
  );
};

export default StockQuantitiesAtDate;
